import type { RelationCountComparator } from '@ofmos/shared/filter-ast';
import type { ViewFilterNode } from '../../data/client';
import { ApiError } from '../../data/http/transport';
import type { RelationContext } from './filterModel';

/**
 * D11's REFUSAL, RECOGNISED IN THE BROWSER - which is the half that turns a dead page into
 * a state somebody can act on.
 *
 * A related node is THE FIRST FILTER NODE WHOSE LEGALITY DEPENDS ON THE READER. Everywhere
 * else `canReadField` answers whatever `canReadObject` answers, so a filter you may not
 * read sits on an object you may not open at all and the list never half-works. A saved
 * view is ONE shared row: an admin saving "Agencies - has any Deal over 10k" as the team
 * default hands every colleague without Deals permission a hard 403 where a list should be.
 *
 * The API still REFUSES, and it must: omitting a LABEL removes something from the response,
 * omitting a PREDICATE adds rows to it, and a silently dropped conjunct is exactly the
 * failure `query-plan.ts` names. What changes is what the browser does with the refusal -
 * it renders it, with the one action that is safe, and it does NOT retry unfiltered.
 */
export interface RelationRefusal {
  /** The object the caller may not read enough of. NOT always the anchor's own object. */
  readonly objectId: string;
  readonly anchorFieldId: string;
}

export function relationRefusalOf(error: unknown): RelationRefusal | null {
  if (!(error instanceof ApiError) || error.code !== 'forbidden') return null;
  const details = error.details;
  if (typeof details !== 'object' || details === null) return null;
  const { objectId, anchorFieldId, site } = details as {
    objectId?: unknown;
    anchorFieldId?: unknown;
    site?: unknown;
  };
  if (site !== 'filter') return null;
  if (typeof objectId !== 'string' || typeof anchorFieldId !== 'string') return null;
  return { objectId, anchorFieldId };
}

/** The related node a refusal is about, found by its anchor, at any depth. */
function nodeFor(
  node: ViewFilterNode | null,
  anchorFieldId: string,
): Extract<ViewFilterNode, { kind: 'related' }> | null {
  if (node === null) return null;
  if (node.kind === 'condition') return null;
  if (node.kind === 'related' && node.anchorFieldId === anchorFieldId) return node;
  for (const child of node.children) {
    const found = nodeFor(child, anchorFieldId);
    if (found !== null) return found;
  }
  return null;
}

/** Any label the loaded anchor sets can give an object id, or `undefined`. */
function labelOf(relations: RelationContext, objectId: string): string | undefined {
  const seen = [relations.objectId, objectId];
  for (const on of seen) {
    const anchor = relations.anchorsOf(on)?.find((entry) => entry.relatedObjectId === objectId);
    if (anchor !== undefined) return anchor.labelPlural;
  }
  for (const anchor of relations.anchorsOf(relations.objectId) ?? []) {
    const nested = relations
      .anchorsOf(anchor.relatedObjectId)
      ?.find((entry) => entry.relatedObjectId === objectId);
    if (nested !== undefined) return nested.labelPlural;
  }
  return undefined;
}

const COUNT_WORDS: Readonly<Record<RelationCountComparator, string>> = {
  gte: 'At least',
  gt: 'More than',
  lt: 'Fewer than',
  lte: 'At most',
  eq: 'Exactly',
  neq: 'Not exactly',
};

/**
 * WHICH REFUSAL THIS IS, IN SECTION 11's WORDS.
 *
 * Four sentences, and the browser can tell them apart from what it already holds - the
 * node the user is looking at and the object id the server named:
 *
 *  - the failing object is NOT the one the anchor reaches, so a hop BENEATH the one they
 *    picked is the restricted one. That is C4's shape, and its sentence names the object
 *    they did not pick, because that is the one they cannot guess;
 *  - the node's own truth value can GAIN matches when the reader sees fewer rows - `none`,
 *    `fewer than`, `at most` - so a partial answer would be wrong rather than partial;
 *  - `exactly` and `not exactly` never stop flipping, at any scope, which is why their
 *    sentence has no "you can see some of them" half;
 *  - otherwise the object is simply not readable at all, which is D11's own sentence.
 */
export function relationRefusalSentence(
  refusal: RelationRefusal,
  filter: ViewFilterNode | null,
  relations: RelationContext,
): string {
  const node = nodeFor(filter, refusal.anchorFieldId);
  const anchor = relations
    .anchorsOf(relations.objectId)
    ?.find((entry) => entry.anchorFieldId === refusal.anchorFieldId);
  const subject = anchor?.label ?? 'related records';
  const failing = labelOf(relations, refusal.objectId) ?? 'another object this filter looks at';

  if (anchor !== undefined && anchor.relatedObjectId !== refusal.objectId) {
    const opening = node?.quantifier === 'none' ? `"Has no ${subject} where…"` : `This filter`;
    return `${opening} needs full access to everything the filter looks at, and this one also looks at ${failing}, which you can only see your own of. Ask for full access to ${failing}, or filter by "has any" instead.`;
  }

  if (node?.quantifier === 'none') {
    return `"Has no ${subject}" is only available to roles that can see all ${subject}. You can see some of them, and a count over part of a list would be wrong rather than partial.`;
  }
  if (node?.quantifier === 'count' && node.count !== undefined) {
    const phrase = `${COUNT_WORDS[node.count.cmp]} ${node.count.n} ${subject}`;
    if (node.count.cmp === 'eq' || node.count.cmp === 'neq') {
      return `"${phrase}" is only available to roles that can see all ${subject}, whichever way the filter is written.`;
    }
    return `"${phrase}" is only available to roles that can see all ${subject}. Counting only the ${subject} you can see would put records on this list that do not belong on it.`;
  }

  return `This view filters on ${subject}, which you do not have access to. Clear the filter to see the list.`;
}
